// The contract with /api/spark (api/src/spark/spark-router.js). Every shape the
// pages assume is here, read off the router and service.

import { apiFetchAuthed } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import type {
  Channel, ReviewState, ReviewStep, Runway, Speciality, Stage, StudioRole, TaskStatus, TeamRole,
} from './sparkRules';

const onUnauthorized = () => useUserStore.getState().logout();
const call = (url: string, options: Record<string, unknown> = {}) => apiFetchAuthed(url, { onUnauthorized, ...options });
const enc = encodeURIComponent;

export interface TeamSlot {
  role: TeamRole;
  user_id: string | null;
  name: string | null;
  approved: boolean;
  /** The person's chosen avatar; null keeps the default face (docs/features/user-avatars.md). */
  avatar_color?: string | null;
  avatar_shape?: string | null;
  avatar_accessory?: string | null;
}

export type ReviewMark = 'pass' | 'now' | 'changes' | 'next';

/** Review Status: the project's review chain at a glance (docs/features/spark.md, "The review chain"). */
export interface ReviewProgress {
  /** A step name: a deliverable waits there, the earliest step when several wait. */
  state: ReviewState;
  /** The chain as the project's team stands now, in order. `merged`: the designer is also the
   *  director, so one step stands for both. A project with no producer has no producer or client step. */
  steps: { step: ReviewStep; merged: boolean; mark: ReviewMark }[];
  /** The deliverable the strip shows (null when nothing has been sent). */
  task: string | null;
  /** How many other deliverables are waiting in review at the same time. */
  more: number;
}

export interface ProductionRow {
  id: string;
  name: string;
  channel: Channel;
  speciality: Speciality;
  stage: Stage;
  status_reason: string | null;
  spice: number;
  runway: Runway | null;
  preview_due: string | null;
  client_name: string | null;
  is_standing: boolean;
  archived_at: string | null;
  archived_reason: 'event_cancelled' | 'manual' | null;
  event_removed_at: string | null;
  show_date: string | null;
  event_count: number;
  deliverables: number;
  ready: number;
  team: TeamSlot[];
  waiting_review: { step: ReviewStep; merged: boolean; name: string | null; task: string } | null;
  review: ReviewProgress;
  flown: boolean;
  open: boolean;
}

export interface LinkedEvent {
  id: string;
  name: string;
  event_date: string;
  status: string;
  location: string | null;
  ls_drone_count: number;
  pyro_drone_count: number;
  preview_links: { label?: string; url: string }[];
  linked_at: string;
}

export interface TeamMember {
  role: TeamRole;
  user_id: string | null;
  name: string | null;
  drafted_at: string;
  drafted_by: string | null;
  approved_at: string | null;
  approved_by: string | null;
  on_crew?: boolean;
  /** The person's chosen avatar; null keeps the default face (docs/features/user-avatars.md). */
  avatar_color?: string | null;
  avatar_shape?: string | null;
  avatar_accessory?: string | null;
}

export interface Task {
  id: string;
  production_id: string;
  parent_id: string | null;
  name: string;
  note: string | null;
  position: number;
  assignee_id: string | null;
  assignee_name: string | null;
  assignee_avatar_color?: string | null;
  assignee_avatar_shape?: string | null;
  assignee_avatar_accessory?: string | null;
  due_date: string | null;
  is_stock: boolean;
  status: TaskStatus;
  moved_from_production_id: string | null;
  moved_from_name: string | null;
  /** Logged minutes. Present for the Studio only. */
  minutes?: number;
}

export interface Review {
  id: string;
  task_id: string;
  kind: 'chain' | 'pilot';
  round: number;
  step: ReviewStep;
  merged: boolean;
  reviewer_id: string | null;
  reviewer_name: string | null;
  ask: string | null;
  status: 'waiting' | 'passed' | 'changes' | 'withdrawn';
  note: string | null;
  requested_by: string | null;
  requested_at: string;
  decided_at: string | null;
  decided_by_name: string | null;
  via: 'app' | 'slack' | null;
}

export interface ProductionDetail {
  production: ProductionRow & {
    own_show_date: string | null;
    deliverable_set: 'shows' | 'systems' | 'motion_graphics' | 'stock';
    ls_drone_count: number | null;
    pyro_drone_count: number | null;
    from_event: boolean;
    pyro_planned: number;
  };
  events: LinkedEvent[];
  team: TeamMember[];
  tasks: Task[];
  reviews: Review[];
  pyro: { id: number; effect: string; count: number }[];
  /** The review order as the team stands now, or why it cannot run. */
  chain: { steps: { step: ReviewStep; reviewer_id: string | null; merged: boolean }[] } | { error: 'no_designer' | 'no_director' };
}

export interface CreativeTabProject extends ProductionDetail {
  pyro_booked: number | null;
}

/** Offered after a link moves the show date: shift open deliverables by `days`? */
export interface DateOffer {
  from: string;
  to: string;
  days: number;
  open_dated: number;
}

export interface Person {
  id: string;
  name: string;
  roles: StudioRole[];
  studio: boolean;
  producer: boolean;
  pilot: boolean;
  approver: boolean;
  /** The person's chosen avatar; null keeps the default face (docs/features/user-avatars.md). */
  avatar_color?: string | null;
  avatar_shape?: string | null;
  avatar_accessory?: string | null;
}

export interface EventHit {
  id: string;
  name: string;
  event_date: string;
  location: string | null;
  status: string;
  created_at: string;
}

export interface TimeEntry {
  id: number;
  task_id: string;
  source: 'timer' | 'manual';
  started_at: string;
  ended_at: string | null;
  confirmed_at: string | null;
  previous: { started_at: string; ended_at: string } | null;
  task_name: string;
  production_id: string;
  production_name: string;
}

export interface MyWork {
  reviews: {
    id: string; kind: 'chain' | 'pilot'; step: ReviewStep; merged: boolean; ask: string | null; requested_at: string;
    task_id: string; task_name: string; production_id: string; production_name: string; requested_by_name: string | null;
  }[];
  tasks: {
    id: string; name: string; note: string | null; due_date: string | null; status: TaskStatus; parent_id: string | null;
    production_id: string; production_name: string; preview_due: string | null;
  }[];
  running: TimeEntry | null;
  to_confirm: TimeEntry[];
}

export interface NewProject {
  name: string;
  speciality: Speciality;
  channel?: Channel;
  work_steps?: string[];
  runway?: Runway | null;
  spice?: number;
  show_date?: string | null;
  client_name?: string | null;
  ls_drone_count?: number | null;
  pyro_drone_count?: number | null;
  event_id?: string | null;
}

export type ProductionPatch = Partial<{
  name: string; channel: Channel; stage: Stage; status_reason: string | null; spice: number; runway: Runway | null;
  show_date: string | null; preview_due: string | null; client_name: string | null;
  ls_drone_count: number | null; pyro_drone_count: number | null; archived: boolean;
}>;

export type TaskPatch = Partial<{
  name: string; note: string | null; assignee_id: string | null; due_date: string | null; is_stock: boolean;
  status: 'not_started' | 'in_progress' | 'ready'; position: number;
}>;

// ── Projects ─────────────────────────────────────────────────────────────────

export const listProductions = (channel?: Channel, closed = false): Promise<{ productions: ProductionRow[] }> =>
  call(`/spark/productions?${new URLSearchParams({ ...(channel ? { channel } : {}), ...(closed ? { closed: '1' } : {}) })}`);

export const getProduction = (id: string): Promise<ProductionDetail> => call(`/spark/productions/${enc(id)}`);

export const createProduction = (body: NewProject): Promise<{ id: string; drawn: unknown; offer: DateOffer | null }> =>
  call('/spark/productions', { method: 'POST', body });

export const updateProduction = (id: string, body: ProductionPatch): Promise<{ ok: true }> =>
  call(`/spark/productions/${enc(id)}`, { method: 'PATCH', body });

// ── Event links ──────────────────────────────────────────────────────────────

/** No query: recently created events. Only events not linked to any project. */
export const searchEvents = (q = ''): Promise<{ events: EventHit[] }> =>
  call(`/spark/events/search${q ? `?q=${enc(q)}` : ''}`);

export const linkEvent = (id: string, eventId: string): Promise<{ offer: DateOffer | null }> =>
  call(`/spark/productions/${enc(id)}/events`, { method: 'POST', body: { event_id: eventId } });

export const unlinkEvent = (id: string, eventId: string): Promise<{ ok: true }> =>
  call(`/spark/productions/${enc(id)}/events/${enc(eventId)}`, { method: 'DELETE' });

export const shiftDeliverables = (id: string, days: number): Promise<{ shifted: number }> =>
  call(`/spark/productions/${enc(id)}/shift`, { method: 'POST', body: { days } });

/** The event page's Creative tab. `project` is null when no Spark project is linked. */
export const getByEvent = (eventId: string): Promise<{ project: CreativeTabProject | null }> =>
  call(`/spark/by-event/${enc(eventId)}`, { silent: true });

// ── Show team ────────────────────────────────────────────────────────────────

export const drawTeam = (id: string, role?: TeamRole): Promise<{ team: Record<TeamRole, string | null>; ties: TeamRole[] }> =>
  call(`/spark/productions/${enc(id)}/draw`, { method: 'POST', body: role ? { role } : {} });

export const approveTeam = (id: string): Promise<{ ok: true; notified: number }> =>
  call(`/spark/productions/${enc(id)}/approve`, { method: 'POST' });

export const setTeamMember = (id: string, role: TeamRole, userId: string | null): Promise<{ ok: true }> =>
  call(`/spark/productions/${enc(id)}/team/${role}`, { method: 'PUT', body: { user_id: userId } });

// ── Deliverables ─────────────────────────────────────────────────────────────

export const addTask = (productionId: string, body: TaskPatch & { name: string; parent_id?: string | null }): Promise<{ task: Task }> =>
  call(`/spark/productions/${enc(productionId)}/tasks`, { method: 'POST', body });

export const updateTask = (taskId: string, body: TaskPatch): Promise<{ task: Task }> =>
  call(`/spark/tasks/${enc(taskId)}`, { method: 'PATCH', body });

export const deleteTask = (taskId: string): Promise<{ ok: true }> =>
  call(`/spark/tasks/${enc(taskId)}`, { method: 'DELETE' });

export const setPyro = (productionId: string, lines: { effect: string; count: number }[]): Promise<{ ok: true }> =>
  call(`/spark/productions/${enc(productionId)}/pyro`, { method: 'PUT', body: { lines } });

// ── Reviews ──────────────────────────────────────────────────────────────────

export const requestReview = (taskId: string, ask?: string): Promise<{ review: Review }> =>
  call(`/spark/tasks/${enc(taskId)}/review`, { method: 'POST', body: { ask: ask || null } });

export const askPilot = (taskId: string, ask?: string): Promise<{ review: Review }> =>
  call(`/spark/tasks/${enc(taskId)}/ask-pilot`, { method: 'POST', body: { ask: ask || null } });

export const answerReview = (reviewId: string, action: 'pass' | 'changes', note?: string): Promise<{ review: Review; next: Review | null }> =>
  call(`/spark/reviews/${enc(reviewId)}/answer`, { method: 'POST', body: { action, note: note || null } });

export const withdrawReview = (reviewId: string): Promise<{ review: Review }> =>
  call(`/spark/reviews/${enc(reviewId)}/withdraw`, { method: 'POST' });

// ── People, roster, workload ─────────────────────────────────────────────────

export const listPeople = (): Promise<{ people: Person[] }> => call('/spark/people');

export const setRoles = (userId: string, roles: StudioRole[]): Promise<{ roles: StudioRole[] }> =>
  call(`/spark/roster/${enc(userId)}`, { method: 'PUT', body: { roles } });

/** Managers only (animator plus manager). Spice, never hours. */
export const getWorkload = (): Promise<{ workload: { id: string; name: string; projects: number; spice: number; avatar_color: string | null; avatar_shape: string | null; avatar_accessory: string | null }[] }> =>
  call('/spark/workload', { silent: true });

export const getYear = (): Promise<{ year: number; flown: number }> => call('/spark/year');

// ── My work and timers ───────────────────────────────────────────────────────

export const getMyWork = (): Promise<MyWork> => call('/spark/me');

export const startTimer = (taskId: string): Promise<{ running: TimeEntry }> =>
  call(`/spark/tasks/${enc(taskId)}/timer`, { method: 'POST' });

/** `needs_confirm` is true when the timer crossed midnight: it then waits on My work. */
export const stopTimer = (): Promise<{ entry: TimeEntry & { needs_confirm: boolean } }> =>
  call('/spark/timer/stop', { method: 'POST' });

export const addTime = (taskId: string, startedAt: string, endedAt: string): Promise<{ id: number }> =>
  call(`/spark/tasks/${enc(taskId)}/time`, { method: 'POST', body: { started_at: startedAt, ended_at: endedAt } });

export const editTime = (entryId: number, startedAt: string, endedAt: string): Promise<{ ok: true }> =>
  call(`/spark/time/${entryId}`, { method: 'PATCH', body: { started_at: startedAt, ended_at: endedAt } });

export const confirmTime = (entryId: number): Promise<{ ok: true }> =>
  call(`/spark/time/${entryId}/confirm`, { method: 'POST' });

// ── The Asana import (admin only; api/src/spark/spark-import.js) ─────────────

export interface AsanaPerson {
  name: string | null;
  /** Lower-cased. */
  email: string | null;
}

export interface AsanaSubtask {
  gid: string;
  /** null: directly under the project. */
  parent_gid: string | null;
  /** 1: directly under the project. */
  depth: number;
  name: string;
  assignee: AsanaPerson | null;
  due_date: string | null;
  completed: boolean;
  notes: string | null;
}

export interface AsanaLine {
  gid: string;
  name: string;
  section: string | null;
  assignee: AsanaPerson | null;
  created: string | null;
  completed: string | null;
  notes: string | null;
  asana: {
    pipeline: string | null;
    status: string | null;
    review: string | null;
    process: string | null;
    speciality: string[];
    spice: string | null;
    drones: number | null;
    pyro: number | null;
    client: string | null;
    show_date: string | null;
    delivery_date: string | null;
    due_date: string | null;
    client_date: string | null;
    start_date: string | null;
    confirmation: string | null;
  };
  /**
   * In FILE order, not tree order: about 4% of Asana's rows come just before their parent, so a
   * subtask can be listed ahead of the task it sits under.
   */
  subtasks: AsanaSubtask[];
  imported: { id: string; name: string } | null;
  /** Spark projects of the same name not imported from Asana: maybe the same show. */
  same_name: { id: string; name: string; show_date: string | null }[];
  /** Unlinked Firefleet events on the line's show date. */
  events: { id: string; name: string; event_date: string; location: string | null }[];
}

/** What an Asana speciality token suggests: a Spark speciality, or work steps. */
export type AsanaSpecialitySuggest = { speciality: Speciality } | { work_steps: string[] };

/** One distinct Asana value and how many lines carry it. Blank reads '(blank)'. */
export interface AsanaValue<S> {
  value: string;
  count: number;
  suggest: S | null;
}

export interface AsanaPreview {
  file: { rows: number; lines: number; hidden_before_2026: number; orphan_subtasks: number };
  values: {
    /** A line with no section counts under '(none)'. */
    sections: { value: string; count: number }[];
    pipeline: AsanaValue<Stage>[];
    /** The suggestion is a Spark status. */
    status: AsanaValue<string>[];
    spice: AsanaValue<number>[];
    /** One row per comma-separated token; a line with none counts under '(blank)'. */
    speciality: AsanaValue<AsanaSpecialitySuggest>[];
    /** `key` is the email, or the name when Asana gave no email. Counts include subtasks. */
    people: { key: string; name: string | null; email: string | null; count: number; user: { id: string; name: string; active: boolean } | null }[];
  };
  lines: AsanaLine[];
}

export interface AsanaTaskImport {
  gid: string;
  name: string;
  /** null, or the gid of a kept task whose own parent_gid is null. Listed after that parent. */
  parent_gid: string | null;
  assignee_id: string | null;
  due_date: string | null;
  status: 'not_started' | 'in_progress' | 'ready';
  note: string | null;
}

export interface AsanaProjectImport {
  name: string;
  channel: Channel;
  speciality: Speciality;
  work_steps?: string[];
  stage: Stage;
  status_reason: string | null;
  spice: number;
  show_date: string | null;
  preview_due: string | null;
  ls_drone_count: number | null;
  pyro_drone_count: number | null;
  client_name: string | null;
}

/** Imports one line, or with `link_to` marks that Spark project as this Asana task and creates nothing. */
export type AsanaLineImport =
  | { gid: string; link_to: string }
  | {
    gid: string;
    project: AsanaProjectImport;
    team?: Partial<Record<TeamRole, string>>;
    event_id?: string;
    /** Only a Shows-channel project with no team given. */
    draw?: boolean;
    tasks: AsanaTaskImport[];
  };

/** Reads the export and writes nothing. Errors carry `data.error.code` and a message to show. */
export const previewAsanaImport = (file: File): Promise<AsanaPreview> => {
  const body = new FormData();
  body.append('file', file);
  return call('/spark/import/asana/preview', { method: 'POST', body });
};

/** 201 `{ id }`, or `{ id, linked: true }` for a link. 409 `already_imported` when the task is in Spark. */
export const importAsanaLine = (body: AsanaLineImport): Promise<{ id: string; linked?: true }> =>
  call('/spark/import/asana/lines', { method: 'POST', body });
